/**
 * Vendor export column spec + thin download wrappers. The actual CSV /
 * Excel / PDF plumbing lives in the shared `lib/table-export` module
 * (reused by bills and any future list export); this file only declares
 * the vendor columns and file names.
 *
 * @see lib/table-export.ts — the shared serialiser
 */
import type { vendors as vendorsContract } from "@flint/api-contract";
import {
  downloadBase64Pdf,
  downloadTableCsv,
  downloadTableXlsx,
  paiseToRupeeNumber,
  type ExportColumn,
} from "../../../lib/table-export.ts";

type ExportRow = vendorsContract.ExportRow;

const yesNo = (b: boolean): string => (b ? "Yes" : "No");

/** One source of truth for the vendor export columns (CSV + Excel). */
const COLUMNS: ExportColumn<ExportRow>[] = [
  { header: "Name", csv: (r) => r.name, xlsx: (r) => r.name },
  { header: "Display name", csv: (r) => r.displayName ?? "", xlsx: (r) => r.displayName ?? "" },
  { header: "GSTIN", csv: (r) => r.gstin ?? "", xlsx: (r) => r.gstin ?? "" },
  { header: "Registration", csv: (r) => r.registrationType, xlsx: (r) => r.registrationType },
  { header: "Contact", csv: (r) => r.contactName ?? "", xlsx: (r) => r.contactName ?? "" },
  { header: "Contact role", csv: (r) => r.contactRole ?? "", xlsx: (r) => r.contactRole ?? "" },
  { header: "Phone", csv: (r) => r.phone ?? "", xlsx: (r) => r.phone ?? "" },
  { header: "Email", csv: (r) => r.email ?? "", xlsx: (r) => r.email ?? "" },
  { header: "Address 1", csv: (r) => r.addressLine1 ?? "", xlsx: (r) => r.addressLine1 ?? "" },
  { header: "Address 2", csv: (r) => r.addressLine2 ?? "", xlsx: (r) => r.addressLine2 ?? "" },
  { header: "City", csv: (r) => r.city ?? "", xlsx: (r) => r.city ?? "" },
  { header: "State", csv: (r) => r.state ?? "", xlsx: (r) => r.state ?? "" },
  { header: "PIN", csv: (r) => r.pincode ?? "", xlsx: (r) => r.pincode ?? "" },
  { header: "Payment terms", csv: (r) => r.paymentTerms ?? "", xlsx: (r) => r.paymentTerms ?? "" },
  { header: "TDS section", csv: (r) => r.tdsSection ?? "", xlsx: (r) => r.tdsSection ?? "" },
  { header: "Job worker", csv: (r) => yesNo(r.isJobWorker), xlsx: (r) => yesNo(r.isJobWorker) },
  { header: "MSME", csv: (r) => yesNo(r.msmeRegistered), xlsx: (r) => yesNo(r.msmeRegistered) },
  { header: "ITC eligible", csv: (r) => yesNo(r.itcEligible), xlsx: (r) => yesNo(r.itcEligible) },
  { header: "RCM default", csv: (r) => yesNo(r.rcmDefault), xlsx: (r) => yesNo(r.rcmDefault) },
  {
    header: "Payment mode",
    csv: (r) => r.defaultPaymentMode ?? "",
    xlsx: (r) => r.defaultPaymentMode ?? "",
  },
  { header: "Category", csv: (r) => r.vendorCategory ?? "", xlsx: (r) => r.vendorCategory ?? "" },
  { header: "Tags", csv: (r) => r.tags.join("; "), xlsx: (r) => r.tags.join("; ") },
  {
    header: "Outstanding (INR)",
    money: true,
    csv: (r) => paiseToRupeeNumber(r.outstandingPaise).toFixed(2),
    xlsx: (r) => paiseToRupeeNumber(r.outstandingPaise),
  },
  {
    header: "YTD spend (INR)",
    money: true,
    csv: (r) => paiseToRupeeNumber(r.ytdSpentPaise).toFixed(2),
    xlsx: (r) => paiseToRupeeNumber(r.ytdSpentPaise),
  },
  { header: "Bills", csv: (r) => String(r.billCount), xlsx: (r) => r.billCount },
  { header: "Last activity", csv: (r) => r.lastActivityAt ?? "", xlsx: (r) => r.lastActivityAt ?? "" },
  { header: "Status", csv: (r) => r.status, xlsx: (r) => r.status },
  { header: "Added on", csv: (r) => r.createdAt.slice(0, 10), xlsx: (r) => r.createdAt.slice(0, 10) },
];

const fileStamp = (generatedAt: string): string => generatedAt.slice(0, 10);

export function downloadVendorsCsv(rows: ExportRow[], generatedAt: string): void {
  downloadTableCsv(COLUMNS, rows, `vendors-${fileStamp(generatedAt)}.csv`);
}

export function downloadVendorsXlsx(rows: ExportRow[], generatedAt: string): Promise<void> {
  return downloadTableXlsx(COLUMNS, rows, {
    sheetName: "Vendors",
    fileName: `vendors-${fileStamp(generatedAt)}.xlsx`,
    isMuted: (r) => r.status === "archived",
  });
}

/** PDF is server-rendered; just download the base64 payload. */
export function downloadVendorsPdf(pdfBase64: string, fileName: string): void {
  downloadBase64Pdf(pdfBase64, fileName);
}
